import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "btc_install_dismissed";

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!isStandalone() && !localStorage.getItem(DISMISS_KEY)) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onManual = () => {
      if (isStandalone()) return;
      localStorage.removeItem(DISMISS_KEY);
      setIosHelp(isIOS());
      setShow(true);
    };
    window.addEventListener("btc-show-install", onManual);

    // iOS never fires beforeinstallprompt — show manual instructions after a short delay
    let t;
    if (isIOS() && !isStandalone() && !localStorage.getItem(DISMISS_KEY)) {
      t = setTimeout(() => setShow(true), 3500);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("btc-show-install", onManual);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (isIOS()) { setIosHelp(true); return; }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    else dismiss();
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-96 z-[60] bg-white border border-[#E8956B]/40 rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.15)] p-4 animate-in slide-in-from-bottom-4">
      <button data-testid="install-dismiss" onClick={dismiss} className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-700">
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <img src="/pwa-icon-192.png" alt="Beauty Trade Club" className="w-12 h-12 rounded-xl shrink-0" />
        <div className="flex-1 pr-4">
          <h3 className="font-display font-bold text-neutral-900 leading-tight">Get the app</h3>
          {iosHelp ? (
            <p data-testid="install-ios-help" className="text-xs text-neutral-600 mt-1 leading-relaxed">
              Tap the <Share size={12} className="inline -mt-0.5 text-[#E8956B]" /> <b>Share</b> button in Safari, then choose <b>“Add to Home Screen”</b> to install Beauty Trade Club.
            </p>
          ) : (
            <p className="text-xs text-neutral-500 mt-1">
              Install Beauty Trade Club on your phone for one-tap access — no app store needed.
            </p>
          )}
          {!iosHelp && (
            <button
              data-testid="install-app-btn"
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-5 py-2 text-sm transition-all"
            >
              <Download size={15} /> {isIOS() ? "How to install" : "Install App"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
